import { useMemo } from 'react'

import { nanoid } from 'nanoid'
import { Navigate } from 'react-router'

import {
  ContentModule,
  ContentModuleType,
  PostType,
  Space,
} from '@tribeplatform/gql-client/types'
import { useContentModuleUtils } from '@tribeplatform/react-components/common'
import { EntityImage } from '@tribeplatform/react-components/common/components'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { getTranslatedPostTypeName } from './translations.js'

interface Props {
  composableContentTypes: (PostType | ContentModule)[]
  space?: Space
  onCancel: () => void
}

export const ContentTypeSelector = ({
  composableContentTypes,
  space,
  onCancel,
}: Props) => {
  const intl = useI18n()
  const { linkToNewContentForm, isPostType } = useContentModuleUtils()
  const items = useMemo(() => {
    return composableContentTypes.map(contentType => ({
      id: nanoid(),
      contentType,
    }))
  }, [composableContentTypes])

  /**
   * We don't have a proper way to check if legacy event CMS is enabled,
   * so we check if there is a post type with the name "Event".
   */
  const hasLegacyEventCMSEnabled = useMemo(() => {
    return composableContentTypes.some(
      content =>
        isPostType(content) &&
        !content.archived &&
        content.name.includes('Event'),
    )
  }, [composableContentTypes, isPostType])

  if (items.length === 1) {
    return (
      <Navigate
        to={linkToNewContentForm({
          contentType: items[0].contentType,
          spaceRelativeUrl: space?.relativeUrl,
        })}
        replace
      />
    )
  }

  return (
    <Card>
      <Card.Header
        title={
          <div className="flex space-s-3 items-center">
            <IconButton
              variant="tertiaryNeutral"
              rounded
              size="lg"
              icon={<SvgIcon className="rtl:-scale-x-100" name="arrow-left" />}
              aria-label={intl.$t({
                id: 'Generics.Back',
                defaultMessage: 'Back',
              })}
              onClick={onCancel}
            ></IconButton>
            <span className="text-xl flex-1">
              <T
                id="Blocks.Composer.CreateButtonText"
                defaultMessage="Create a new"
              />
            </span>
          </div>
        }
      />
      <Card.Content>
        <List divider spacing="none" className="border-t border-line-subdued">
          {items?.map(item => (
            <ComposableContentItem
              key={item.id}
              contentType={item.contentType}
              space={space}
              hasLegacyEventCMSEnabled={hasLegacyEventCMSEnabled}
            />
          ))}
        </List>
      </Card.Content>
    </Card>
  )
}

interface ComposableContentItemProps {
  contentType: PostType | ContentModule
  space?: Space
  hasLegacyEventCMSEnabled: boolean
}

const ComposableContentItem = ({
  contentType,
  space,
  hasLegacyEventCMSEnabled,
}: ComposableContentItemProps) => {
  const { Link } = useRouter()
  const { linkToNewContentForm, isPostType } = useContentModuleUtils()

  return (
    <List.Item>
      <Link
        variant="inherit"
        className="hover:bg-surface-hovered px-4 py-5 sm:p-6 flex space-s-3 items-center"
        href={linkToNewContentForm({
          contentType,
          spaceRelativeUrl: space?.relativeUrl,
        })}
      >
        {isPostType(contentType) ? (
          <PostTypeItem postType={contentType} />
        ) : (
          <ContentModuleItem
            contentModule={contentType}
            hasLegacyEventCMSEnabled={hasLegacyEventCMSEnabled}
          />
        )}
        <SvgIcon className="w-6 h-6 rtl:-scale-x-100" name="chevron-right" />
      </Link>
    </List.Item>
  )
}

const PostTypeItem = ({ postType }: { postType: PostType }) => {
  const intl = useI18n()
  return (
    <>
      <EntityImage
        size="xl"
        entity={{ image: postType.icon, name: postType.name }}
      />
      <span className="flex-1">
        {getTranslatedPostTypeName(intl, postType.name)}
      </span>
    </>
  )
}

interface ContentModuleItemProps {
  contentModule: ContentModule
  hasLegacyEventCMSEnabled: boolean
}

const ContentModuleItem = ({
  contentModule,
  hasLegacyEventCMSEnabled,
}: ContentModuleItemProps) => {
  const { $t } = useI18n()
  const text = useMemo(() => {
    switch (contentModule.contentModuleType) {
      case ContentModuleType.event:
        return $t({
          id: 'Generics.Event',
          defaultMessage: 'Event',
        })
      default: {
        const _exhaustive: never = contentModule.contentModuleType
        return _exhaustive
      }
    }
  }, [$t, contentModule.contentModuleType])

  if (ContentModuleType.event === contentModule.contentModuleType) {
    return (
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <SvgIcon name="calendar" size="xl" />
          {text}
          {hasLegacyEventCMSEnabled && (
            <Badge
              size="sm"
              variant="positive"
              leadingIcon={<SvgIcon name="space" size="sm" />}
            >
              <T
                id="Composer.ContentTypeSelector.EventModule.BadgeText"
                defaultMessage="Easy events"
              />
            </Badge>
          )}
        </div>
      </div>
    )
  }

  return <span className="flex-1">{text}</span>
}
